import { MenuItem } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { TimeField } from '@superdispatch/dates';
import {
  FormikCurrencyField,
  FormikDateField,
  FormikTextField,
  useFormikEnhanced,
} from '@superdispatch/forms';
import { Column, Columns, Stack, useSnackbarStack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useMemo } from 'react';
import { castAPIData } from 'shared/api/APISchema';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { SlowFormikDrawer } from 'shared/form/SlowFormikDrawer';
import { useResource } from 'shared/helpers/Resource';
import { validateNumber } from 'shared/helpers/ValidationHelpers';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import {
  LoadDTO,
  LoadMarkAsPaidDTO,
  loadMarkAsPaidSchema,
} from '../data/LoadDTO';
import { useLoadsContext } from '../data/LoadsContext';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import { useSubhaulLoad } from '../view/useSubhaulLoad';
import { useLoadsCache } from '../data/LoadsAPI';
import { formatCarrierPaymentMethod } from 'shared/helpers/CarrierPaymentTypes';

interface LoadMarkAsPaidDrawerProps {
  load?: LoadDTO;
  isLoading?: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

export function LoadMarkAsPaidDrawer({
  load,
  onClose,
  isLoading,
  onSubmitSuccess,
}: LoadMarkAsPaidDrawerProps) {
  const loadGUID = load?.guid || '';
  const { data } = useCarrierSettings();
  const { loadsAPI } = useLoadsContext();
  const { invalidateLoads } = useLoadsCache();
  const { addSnackbar } = useSnackbarStack();
  const { isSubhauled } = useSubhaulLoad();
  const currentDate = useMemo(() => new Date().toISOString(), []);

  const method = useMemo(() => {
    if (isSubhauled) {
      return load?.customer_payment?.paid_method;
    }
    return load?.payments && load.payments.length > 0
      ? load?.payments[0]?.method
      : null;
  }, [load?.payments, load?.customer_payment?.paid_method, isSubhauled]);

  const price = useMemo(() => {
    if (isSubhauled) {
      return load?.customer_payment?.price;
    }
    return load?.payments && load.payments.length > 0
      ? load?.payments[0]?.price
      : null;
  }, [load?.payments, load?.customer_payment?.price, isSubhauled]);

  const notes = useMemo(() => {
    if (isSubhauled) {
      return load?.customer_payment?.payment_notes;
    }
    return load?.payments && load.payments.length > 0
      ? load?.payments[0]?.notes
      : null;
  }, [load?.payments, load?.customer_payment?.payment_notes, isSubhauled]);

  const paidMethodOptions = useMemo(
    () =>
      data?.paid_methods.filter((paidMethod) => {
        return !(
          method !== 'superpay' && paidMethod.text.toLowerCase() === 'superpay'
        );
      }),
    [data?.paid_methods, method],
  );

  const paidMethodID = useMemo(() => {
    if (method) {
      const formattedMethod = formatCarrierPaymentMethod(method);
      const paidMethod = paidMethodOptions?.find(
        ({ text }) => formattedMethod === text,
      );
      return paidMethod?.value || null;
    }
    return null;
  }, [method, paidMethodOptions]);

  const quickbooksSettings = useResource(
    () => (!loadGUID ? undefined : loadsAPI.getQuickBooksSettings()),
    [loadsAPI, loadGUID],
  );

  const loadInvoiceInfo = useResource(
    () => (!loadGUID ? undefined : loadsAPI.getLoadInvoiceInfo(loadGUID)),
    [loadsAPI, loadGUID],
  );

  const formik = useFormikEnhanced<LoadMarkAsPaidDTO, APIResponse>({
    initialValues: {
      paid_amount: isSubhauled
        ? Number(load?.customer_payment?.paid_amount || 0)
        : Number(price || 0),
      paid_method: paidMethodID,
      payment_notes: notes || '',
      payment_reference_number: isSubhauled
        ? load?.customer_payment?.payment_reference_number || ''
        : '',
      receipt_date: currentDate,
    },
    onSubmit: (values) =>
      loadsAPI.markAsPaid(loadGUID, castAPIData(loadMarkAsPaidSchema, values)),
    onSubmitSuccess: ({ user_message }) => {
      addSnackbar(user_message || 'Marked as paid.', { variant: 'success' });
      onSubmitSuccess();
      invalidateLoads();
    },
    onSubmitFailure: ({ message }) => {
      addSnackbar(message, { variant: 'error' });
    },
  });

  return (
    <SlowFormikDrawer
      formik={formik}
      title="Mark as Paid"
      open={!!load || !!isLoading}
      onClose={onClose}
      actions={
        <Box paddingLeft="large">
          <PermissionButton
            name="loads"
            type="submit"
            permission="can_mark_as_paid"
            pending={formik.isSubmitting}
            disabled={isLoading}
          >
            Save
          </PermissionButton>
        </Box>
      }
    >
      {isLoading ? (
        <Stack>
          <Skeleton variant="rect" height={56} />
          <Skeleton variant="rect" height={56} />
          <Skeleton variant="rect" height={56} />
          <Skeleton variant="rect" height={56} />
          <Skeleton variant="rect" height={56} />
        </Stack>
      ) : (
        <Stack>
          <FormikCurrencyField
            fullWidth={true}
            name="paid_amount"
            label="Paid Amount"
            validate={(value) => validateNumber(value, { min: 0 })}
            helperText={
              quickbooksSettings.data?.connected &&
              loadInvoiceInfo.data?.data.qb_desktop.connected &&
              'To sync with QuickBooks specify paid amount'
            }
          />

          <FormikTextField
            select={true}
            name="paid_method"
            fullWidth={true}
            label="Paid Method"
            validate={(value) =>
              value != null && value !== '' ? undefined : 'Select method'
            }
          >
            {paidMethodOptions?.map(({ value, text }) => (
              <MenuItem key={value} value={value}>
                {text}
              </MenuItem>
            ))}
          </FormikTextField>

          <FormikTextField
            fullWidth={true}
            name="payment_reference_number"
            label="Reference Number"
          />

          <FormikDateField
            fullWidth={true}
            variant="DateTime"
            label="Receipt Date"
            name="receipt_date"
            enableClearable={true}
            disableCloseOnSelect={true}
            renderFooter={(api) => (
              <Columns space="small">
                <Column>
                  <TimeField
                    fullWidth={true}
                    placeholder="Select Time"
                    value={api.dateValue}
                    onChange={({ dateValue }) => {
                      api.change(dateValue);
                    }}
                  />
                </Column>
                <Column width="content">
                  <Button onClick={api.close}>Save</Button>
                </Column>
              </Columns>
            )}
          />

          <FormikTextField
            multiline={true}
            fullWidth={true}
            name="payment_notes"
            label="Payment Notes"
          />
        </Stack>
      )}
    </SlowFormikDrawer>
  );
}
